/**
 * EmailAuditCard — admin tool that shows every email the backend sent
 * (or tried to send), the code path that triggered it, and an
 * admin-controlled suppression list to permanently block bad emails.
 *
 * Lives under the System tab in /admin.
 */
import React, { useEffect, useState } from 'react';
import {
  ShieldCheck, Loader2, RefreshCw, AlertTriangle, Plus, Trash2,
  Eye, CheckCircle2, XCircle, X, Search,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SUPP_TYPES = [
  { value: 'subjectContains', label: 'Subject contains' },
  { value: 'recipient',       label: 'Recipient email is' },
  { value: 'codepath',        label: 'Codepath contains' },
  { value: 'source',          label: 'Source contains' },
];

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

export default function EmailAuditCard({ adminToken }) {
  const [log, setLog] = useState([]);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Filters
  const [fSubject, setFSubject] = useState('');
  const [fRecipient, setFRecipient] = useState('');
  const [fCodepath, setFCodepath] = useState('');
  const [fOnlyFailed, setFOnlyFailed] = useState(false);

  // Detail modal
  const [detail, setDetail] = useState(null);

  // New rule form
  const [showAddRule, setShowAddRule] = useState(false);
  const [ruleType, setRuleType] = useState('subjectContains');
  const [ruleValue, setRuleValue] = useState('');
  const [ruleReason, setRuleReason] = useState('');
  const [addingRule, setAddingRule] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const loadAll = async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (fSubject)   params.set('subjectContains', fSubject);
      if (fRecipient) params.set('recipient', fRecipient);
      if (fCodepath)  params.set('codepath', fCodepath);
      if (fOnlyFailed) params.set('success', 'false');
      const [lRes, sRes, rRes] = await Promise.all([
        fetch(`${API}/api/admin/email-audit/log?${params.toString()}`, { headers }),
        fetch(`${API}/api/admin/email-audit/stats`, { headers }),
        fetch(`${API}/api/admin/email-audit/suppressions`, { headers }),
      ]);
      if (!lRes.ok) throw new Error(`Log load failed: ${lRes.status}`);
      if (!sRes.ok) throw new Error(`Stats load failed: ${sRes.status}`);
      if (!rRes.ok) throw new Error(`Rules load failed: ${rRes.status}`);
      const [lData, sData, rData] = await Promise.all([lRes.json(), sRes.json(), rRes.json()]);
      setLog(lData.log || []);
      setStats(sData);
      setRules(rData.rules || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openDetail = async (id) => {
    setDetail({ id, loading: true });
    try {
      const res = await fetch(`${API}/api/admin/email-audit/log/${id}`, { headers });
      if (!res.ok) throw new Error(`Load detail failed: ${res.status}`);
      setDetail(await res.json());
    } catch (e) {
      setDetail({ error: e.message });
    }
  };

  const addRule = async () => {
    if (!ruleValue.trim()) { setErr('Enter a value first'); return; }
    setAddingRule(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/email-audit/suppressions`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: ruleType, value: ruleValue.trim(), reason: ruleReason.trim() || null }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Add failed (${res.status}): ${t.slice(0, 160)}`);
      }
      setRuleValue('');
      setRuleReason('');
      setShowAddRule(false);
      await loadAll();
    } catch (e) {
      setErr(e.message);
    } finally {
      setAddingRule(false);
    }
  };

  const deleteRule = async (rid) => {
    if (!window.confirm('Delete this suppression rule? Emails that previously matched it can be sent again.')) return;
    try {
      const res = await fetch(`${API}/api/admin/email-audit/suppressions/${rid}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      await loadAll();
    } catch (e) {
      setErr(e.message);
    }
  };

  // Quick-block from a log row
  const blockBySubject = (subject) => {
    setRuleType('subjectContains');
    setRuleValue(subject);
    setRuleReason(`Blocked from audit log on ${new Date().toLocaleDateString()}`);
    setShowAddRule(true);
  };

  return (
    <div data-testid="email-audit-card"
         className="bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="bg-slate-800 text-white rounded-lg p-2.5">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-slate-900">Outgoing Email Audit & Suppression</h3>
          <p className="text-xs text-slate-700 mt-0.5 max-w-3xl leading-snug">
            Every email the backend sends or tries to send is logged here with the exact code path that triggered it.
            Add suppression rules to permanently block specific subjects, recipients, code paths, or sources — useful when
            a buggy code path (like the SamCart webhook firing the "Cancel Standalone Billing" email on standalone purchases)
            needs to be stopped immediately while we ship a fix.
          </p>
        </div>
      </div>

      {/* KPI strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="email-audit-kpis">
          <div className="bg-white border border-slate-200 rounded-lg p-2 text-center">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total sent</p>
            <p className="text-xl font-extrabold text-slate-900">{stats.byStatus?.sent || 0}</p>
          </div>
          <div className="bg-white border border-rose-200 rounded-lg p-2 text-center">
            <p className="text-[10px] uppercase tracking-wider font-bold text-rose-600">Failed / blocked</p>
            <p className="text-xl font-extrabold text-rose-700">{stats.byStatus?.failed || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-2 text-center">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Active rules</p>
            <p className="text-xl font-extrabold text-slate-900">{rules.filter(r => r.enabled).length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-2 text-center">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Last 14 days</p>
            <p className="text-xl font-extrabold text-slate-900">{(stats.byDay || []).reduce((a, d) => a + d.count, 0)}</p>
          </div>
        </div>
      )}

      {/* Filters + actions */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-[10px] font-bold text-slate-600 block">Subject contains</label>
          <input value={fSubject} onChange={e => setFSubject(e.target.value)}
                 placeholder="e.g. Cancel Standalone"
                 className="px-2 py-1 border border-slate-200 rounded text-sm focus:border-blue-500 outline-none"
                 data-testid="email-audit-filter-subject" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-600 block">Recipient</label>
          <input value={fRecipient} onChange={e => setFRecipient(e.target.value)}
                 placeholder="user@example.com"
                 className="px-2 py-1 border border-slate-200 rounded text-sm focus:border-blue-500 outline-none"
                 data-testid="email-audit-filter-recipient" />
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-600 block">Codepath</label>
          <input value={fCodepath} onChange={e => setFCodepath(e.target.value)}
                 placeholder="samcart_webhook"
                 className="px-2 py-1 border border-slate-200 rounded text-sm focus:border-blue-500 outline-none"
                 data-testid="email-audit-filter-codepath" />
        </div>
        <label className="flex items-center gap-1 text-xs font-bold text-slate-700 cursor-pointer">
          <input type="checkbox" checked={fOnlyFailed}
                 onChange={e => setFOnlyFailed(e.target.checked)}
                 data-testid="email-audit-only-failed" />
          Only failed/blocked
        </label>
        <button onClick={loadAll} disabled={loading}
                className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-bold px-3 py-1.5 rounded inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="email-audit-search">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Search
        </button>
        <button onClick={loadAll} disabled={loading}
                className="bg-white border-2 border-slate-300 text-slate-800 text-sm font-bold px-3 py-1.5 rounded inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="email-audit-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded-lg p-3 flex items-start gap-2"
             data-testid="email-audit-error">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      {/* Log table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-900">
            <tr>
              <th className="text-left px-3 py-2 font-bold w-44">When</th>
              <th className="text-left px-3 py-2 font-bold">Subject → Recipient</th>
              <th className="text-left px-3 py-2 font-bold w-60">Codepath</th>
              <th className="text-center px-3 py-2 font-bold w-24">Status</th>
              <th className="text-right px-3 py-2 font-bold w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {log.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-slate-500 text-sm py-6">No emails matched. Hit Search after sending one.</td></tr>
            ) : log.map(row => (
              <tr key={row.id} className="border-t border-slate-100 hover:bg-blue-50/40"
                  data-testid={`email-audit-row-${row.id}`}>
                <td className="px-3 py-2 text-xs text-slate-700 align-top">{fmt(row.sentAt)}</td>
                <td className="px-3 py-2 align-top">
                  <p className="font-bold text-slate-900 text-sm leading-tight truncate max-w-[400px]"
                     title={row.subject}>{row.subject || '(no subject)'}</p>
                  <p className="text-xs text-slate-500 truncate max-w-[400px]" title={row.recipient}>
                    {row.sender} → {row.recipient}
                  </p>
                  {row.errorMessage && (
                    <p className="text-[11px] text-rose-700 mt-0.5 truncate max-w-[400px]" title={row.errorMessage}>
                      {row.errorMessage}
                    </p>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-600 align-top truncate max-w-[220px]"
                    title={row.codepath}>
                  {row.codepath}
                </td>
                <td className="px-3 py-2 text-center align-top">
                  {row.success
                    ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-bold">
                        <CheckCircle2 className="w-3.5 h-3.5" /> sent
                      </span>
                    : <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-bold">
                        <XCircle className="w-3.5 h-3.5" /> {row.errorMessage?.startsWith('SUPPRESSED') ? 'blocked' : 'failed'}
                      </span>}
                </td>
                <td className="px-3 py-2 text-right align-top whitespace-nowrap">
                  <button onClick={() => openDetail(row.id)}
                          className="text-[11px] text-blue-700 hover:text-blue-900 font-bold px-1.5 py-0.5 rounded hover:bg-blue-50 inline-flex items-center gap-1"
                          data-testid={`email-audit-view-${row.id}`}>
                    <Eye className="w-3 h-3" /> View
                  </button>
                  <button onClick={() => blockBySubject(row.subject)}
                          className="text-[11px] text-rose-700 hover:text-rose-900 font-bold px-1.5 py-0.5 rounded hover:bg-rose-50 inline-flex items-center gap-1 ml-1"
                          data-testid={`email-audit-block-${row.id}`}>
                    <ShieldCheck className="w-3 h-3" /> Block
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Suppression rules */}
      <div className="bg-white border-2 border-rose-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-extrabold text-rose-900 flex items-center gap-1">
            <ShieldCheck className="w-4 h-4" /> Active suppression rules ({rules.length})
          </h4>
          <button onClick={() => setShowAddRule(s => !s)}
                  className="text-sm bg-rose-600 hover:bg-rose-700 text-white font-bold px-3 py-1 rounded inline-flex items-center gap-1"
                  data-testid="email-audit-add-rule-btn">
            <Plus className="w-3.5 h-3.5" /> {showAddRule ? 'Cancel' : 'Add rule'}
          </button>
        </div>

        {showAddRule && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 mb-3 space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-bold text-rose-900 block mb-0.5">Match type</label>
                <select value={ruleType} onChange={e => setRuleType(e.target.value)}
                        className="w-full px-2 py-1 border border-rose-200 rounded text-sm bg-white focus:border-rose-500 outline-none"
                        data-testid="email-audit-rule-type">
                  {SUPP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold text-rose-900 block mb-0.5">Value (case-insensitive)</label>
                <input value={ruleValue} onChange={e => setRuleValue(e.target.value)}
                       placeholder='e.g. "Cancel Standalone Billing"'
                       className="w-full px-2 py-1 border border-rose-200 rounded text-sm focus:border-rose-500 outline-none"
                       data-testid="email-audit-rule-value" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold text-rose-900 block mb-0.5">Why are you blocking this? (optional)</label>
              <input value={ruleReason} onChange={e => setRuleReason(e.target.value)}
                     placeholder='e.g. "Webhook bug — fires on standalone Smile Design AI purchases"'
                     className="w-full px-2 py-1 border border-rose-200 rounded text-sm focus:border-rose-500 outline-none"
                     data-testid="email-audit-rule-reason" />
            </div>
            <button onClick={addRule} disabled={addingRule || !ruleValue.trim()}
                    className="bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold px-4 py-1.5 rounded inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="email-audit-rule-save">
              {addingRule ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Add rule
            </button>
            <p className="text-[11px] text-rose-800">
              Rules take effect within 30s on all backend code paths (broadcasts, webhooks, drip sequences, etc.).
            </p>
          </div>
        )}

        {rules.length === 0 ? (
          <p className="text-xs text-slate-500 py-2">No suppression rules. Use "Block" on a log row or "Add rule" above.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left px-2 py-1 font-bold w-32">Type</th>
                <th className="text-left px-2 py-1 font-bold">Value</th>
                <th className="text-left px-2 py-1 font-bold">Why</th>
                <th className="text-right px-2 py-1 font-bold w-16">Blocked</th>
                <th className="text-right px-2 py-1 font-bold w-16"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`email-audit-rule-${r.id}`}>
                  <td className="px-2 py-1 text-xs text-slate-700">
                    <span className="inline-block bg-rose-100 text-rose-800 font-bold rounded px-1.5">{r.type}</span>
                  </td>
                  <td className="px-2 py-1 text-xs font-mono text-slate-900">{r.value}</td>
                  <td className="px-2 py-1 text-xs text-slate-600 max-w-[300px] truncate" title={r.reason}>{r.reason || '—'}</td>
                  <td className="px-2 py-1 text-xs text-right font-bold text-slate-900">{r.blockedCount || 0}</td>
                  <td className="px-2 py-1 text-right">
                    <button onClick={() => deleteRule(r.id)}
                            className="text-rose-700 hover:text-rose-900 p-1 rounded hover:bg-rose-50"
                            title="Delete rule"
                            data-testid={`email-audit-rule-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[1000] bg-slate-900/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }}
             data-testid="email-audit-detail">
          <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="text-sm font-extrabold text-slate-900">Email detail</h3>
              <button onClick={() => setDetail(null)}
                      className="text-slate-500 hover:text-slate-800 p-1 rounded hover:bg-slate-100"
                      data-testid="email-audit-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-5 space-y-3 text-sm">
              {detail.loading && <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" />}
              {detail.error && <p className="text-rose-700">{detail.error}</p>}
              {detail.id && !detail.loading && !detail.error && (
                <>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><b className="text-slate-500">Subject:</b><br/>{detail.subject}</div>
                    <div><b className="text-slate-500">Sent at:</b><br/>{fmt(detail.sentAt)}</div>
                    <div><b className="text-slate-500">From:</b><br/>{detail.sender}</div>
                    <div><b className="text-slate-500">To:</b><br/>{detail.recipient}</div>
                    <div className="col-span-2"><b className="text-slate-500">Codepath:</b><br/><code className="bg-slate-100 px-1 rounded text-[11px]">{detail.codepath}</code></div>
                    <div className="col-span-2"><b className="text-slate-500">Source:</b> {detail.source}</div>
                    <div className="col-span-2"><b className="text-slate-500">Status:</b>{' '}
                      {detail.success
                        ? <span className="text-emerald-700 font-bold">sent</span>
                        : <span className="text-rose-700 font-bold">{detail.errorMessage?.startsWith('SUPPRESSED') ? 'blocked' : 'failed'}</span>}
                    </div>
                    {detail.errorMessage && (
                      <div className="col-span-2"><b className="text-slate-500">Error / reason:</b><br/>
                        <span className="text-rose-700">{detail.errorMessage}</span></div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-600 mb-1">HTML preview (first {detail.htmlBytes} bytes captured)</p>
                    <iframe title="Email preview" srcDoc={detail.htmlPreview || ''} sandbox=""
                            className="w-full h-[420px] border border-slate-200 rounded bg-white" />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
